import React from 'react';
import { Popover } from '@headlessui/react';
import { Link, Typography, performLogout } from '@loomstateorg/shared-frontend';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { useAccount } from 'frontastic';

export interface LoggedInProps {
  closePopover?: () => void;
  className?: string;
  rowClassName?: string;
  rowButtonClassName?: string;
  logoutTriggerClassName?: string;
}

export const DEFAULT_ACCOUNT_DROPDOWN_ROW_CLASSNAME =
  'w-full rounded-md border-0 px-14 py-10 outline-none ring-0 transition-colors hover:bg-nav-highlight active:border-0 active:bg-nav-highlight focus-within:ring-0';

export const DEFAULT_ACCOUNT_DROPDOWN_ROW_BUTTON_CLASSNAME =
  'flex min-h-[30px] w-full items-center border-0 bg-transparent p-0 text-left shadow-none outline-none ring-0 [-webkit-tap-highlight-color:transparent] hover:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 active:border-0 focus-visible:outline-none focus-visible:ring-0';

export const DEFAULT_ACCOUNT_DROPDOWN_LOGOUT_TRIGGER_CLASSNAME =
  'flex min-h-[50px] w-full items-center justify-start border-0 bg-transparent text-left shadow-none outline-none ring-0 [-webkit-tap-highlight-color:transparent] focus:outline-none focus:ring-0 active:border-0 focus-visible:outline-none focus-visible:ring-0';

const LoggedIn = ({
  closePopover,
  className = 'flex w-160 flex-col rounded-lg',
  rowClassName: rowSurfaceClassName = DEFAULT_ACCOUNT_DROPDOWN_ROW_CLASSNAME,
  rowButtonClassName: popoverButtonResetClassName = DEFAULT_ACCOUNT_DROPDOWN_ROW_BUTTON_CLASSNAME,
  logoutTriggerClassName: popoverButtonRowTriggerClassName = DEFAULT_ACCOUNT_DROPDOWN_LOGOUT_TRIGGER_CLASSNAME,
}: LoggedInProps) => {
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { logout } = useAccount();

  const handleLogout = async () => {
    closePopover?.();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });

      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => caches.delete(cacheName));
      });
    }

    await performLogout(null, logout);
  };

  return (
    <div className={className} data-testid="account-dropdown">
      <Link
        link="/account#"
        className={`group block text-left ${rowSurfaceClassName}`}
        onClick={() => closePopover?.()}
        data-testid="my-account"
      >
        {/* testid-ignore: only closes the popover; the wrapping Link carries the testid */}
        <Popover.Button className={popoverButtonResetClassName}>
          <Typography
            fontSize={14}
            fontFamily="primaryThin"
            className="text-primary-regular underline-offset-4 group-hover:underline"
          >
            {formatAccountMessage({ id: 'my.account', defaultMessage: 'My Account' })}
          </Typography>
        </Popover.Button>
      </Link>

      <Link
        link="/account#support"
        className={`group block text-left ${rowSurfaceClassName}`}
        onClick={() => closePopover?.()}
        data-testid="customer-support"
      >
        {/* testid-ignore: only closes the popover; the wrapping Link carries the testid */}
        <Popover.Button className={popoverButtonResetClassName}>
          <Typography
            fontSize={14}
            fontFamily="primaryThin"
            className="text-primary-regular underline-offset-4 group-hover:underline"
          >
            {formatAccountMessage({ id: 'customer.support', defaultMessage: 'Customer Support' })}
          </Typography>
        </Popover.Button>
      </Link>

      <Popover.Button
        type="button"
        onClick={handleLogout}
        className={`group ${rowSurfaceClassName} ${popoverButtonRowTriggerClassName}`}
        data-testid="sign-out"
      >
        <Typography
          fontSize={14}
          fontFamily="primaryThin"
          className="text-primary-regular underline-offset-4 group-hover:underline"
        >
          {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign out' })}
        </Typography>
      </Popover.Button>
    </div>
  );
};

export default LoggedIn;
